/**
 * Statusleiste am oberen Bildrand (VISUAL_REFERENCE Abschnitt 16).
 *
 * Die Reihenfolge ist verbindlich und bildet die "Matchachse" des Spiels:
 *
 *   eigenes Portal | Gold | Income | Income-Timer | MATCHZEIT | Army-Timer | Gegnerportal
 *
 * Tier und Belegung der gegnerischen Lane standen bis v0.51 rechts neben der
 * Matchzeit. Sie sind raus (Produktentscheidung vom 30.09.2026): Beides steht
 * im Army-Fenster, und dort besser - die Tier-Reiter zeigen auch, welche
 * Stufen gesperrt sind, und die Lane-Zeile rechnet den aktuellen Entwurf mit.
 *
 * Links das eigene Ziel in Blau, rechts das gegnerische in Rot, in der Mitte
 * die Matchzeit als auffaelligster Wert. Wer oben nach links schaut, sieht
 * seinen Stand; wer nach rechts schaut, sieht sein Ziel.
 *
 * Alles hier ist reine Anzeige. Gerechnet wird ausschliesslich auf dem Server.
 */

import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { formatGold, getIncomeCountdown, getWaveCooldown } from './economyView.js';

/** Minuten:Sekunden, zweistellig - wie eine Spieluhr. */
function formatClock(seconds: number): string {
  const gesamt = Math.max(0, Math.floor(seconds));
  const minuten = Math.floor(gesamt / 60);
  const rest = gesamt % 60;
  return `${minuten.toString().padStart(2, '0')}:${rest.toString().padStart(2, '0')}`;
}

/**
 * Portalbalken.
 *
 * Zur Zahl gehoert immer auch der Balken und die Farbe - aber nie die Farbe
 * allein (Masterprompt Abschnitt 40).
 */
function PortalBar({
  value,
  max,
  side,
  label,
}: {
  value: number;
  max: number;
  side: 'own' | 'enemy';
  label: string;
}) {
  const anteil = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;

  return (
    <div className={`dl-topbar__portal dl-topbar__portal--${side}`}>
      <span className="dl-topbar__portal-label">{label}</span>
      <div className="dl-topbar__portal-track">
        <div
          className={`dl-topbar__portal-fill dl-topbar__portal-fill--${side}`}
          style={{ width: `${anteil * 100}%` }}
        />
        <span className="dl-topbar__portal-value">
          {Math.ceil(value)} / {Math.round(max)}
        </span>
      </div>
    </div>
  );
}

export function TopBar({ snapshot }: { snapshot: MatchSnapshot }) {
  const countdown = getIncomeCountdown(snapshot.phase, snapshot.elapsedSeconds);

  /*
   * Army-Cooldown rechts der Matchzeit (Produktentscheidung vom 30.09.2026).
   *
   * Vorher stand die Wartezeit nur im Army-Fenster - man musste es also
   * aufmachen, um zu sehen, ob ueberhaupt geschickt werden kann.
   *
   * Sie stand zuerst links neben dem Income-Tick. Dort schob sie die
   * Wirtschaftsgruppe weiter unter die absolut zentrierte Matchzeit, die auf
   * schmalen Geraeten ohnehin schon darueberlag. Rechts ist Platz frei.
   *
   * Zweizeilig wie die Portalbloecke: Beschriftung oben, Wert darunter.
   */
  const wellenWartezeit = getWaveCooldown(snapshot.phase, snapshot.waveCooldown);
  const wellenBereit = wellenWartezeit === 0;

  return (
    <div className="dl-topbar">
      <PortalBar
        value={snapshot.portalHp}
        max={snapshot.portalMaxHp}
        side="own"
        label={t.hud.yourPortal}
      />

      <div className="dl-topbar__economy">
        <div className="dl-topbar__item dl-topbar__item--gold" title={t.economy.gold}>
          <span className="dl-topbar__icon" aria-hidden="true">
            ◉
          </span>
          <span className="dl-topbar__value">{formatGold(snapshot.gold)}</span>
        </div>

        <div className="dl-topbar__item dl-topbar__item--income" title={t.economy.income}>
          <span className="dl-topbar__icon" aria-hidden="true">
            ▲
          </span>
          <span className="dl-topbar__value">+{formatGold(snapshot.income)}</span>
        </div>

        <div className="dl-topbar__item dl-topbar__item--timer" title={t.economy.nextIncome}>
          <span className="dl-topbar__icon" aria-hidden="true">
            ◷
          </span>
          <span className="dl-topbar__value">{countdown === null ? '-' : `${countdown}s`}</span>
        </div>
      </div>

      {/* Die Matchzeit steht in der Mitte und ist der auffaelligste Wert. */}
      <div className="dl-topbar__clock" title={t.hud.matchTime}>
        {formatClock(snapshot.elapsedSeconds)}
      </div>

      {/* Wartezeit bis zur naechsten Welle. Zwischen Uhr und Gegnerportal,
          weil links der Platz fehlt - siehe oben. */}
      <div
        className={'dl-topbar__wave' + (wellenBereit ? ' dl-topbar__wave--ready' : '')}
        title={wellenBereit ? t.economy.waveReady : t.economy.waveCooldown}
      >
        <span className="dl-topbar__wave-label">{t.economy.waveLabel}</span>
        <span className="dl-topbar__wave-value">
          {wellenWartezeit === null
            ? '-'
            : wellenBereit
              ? t.economy.waveReadyShort
              : `${wellenWartezeit}s`}
        </span>
      </div>

      {/* Das gegnerische Portal ist das Ziel - es steht ganz rechts. Ohne
          Gegner im Match gibt es hier noch nichts zu zeigen. */}
      <PortalBar
        value={snapshot.opponent?.portalHp ?? snapshot.portalMaxHp}
        max={snapshot.portalMaxHp}
        side="enemy"
        label={t.hud.enemyPortal}
      />
    </div>
  );
}
